import {
  add,
  eachMonthOfInterval,
  endOfDay,
  endOfMonth,
  endOfYear,
  setYear,
  startOfDay,
  startOfMonth,
  startOfYear,
  sub,
} from './index'

export interface Timespan {
  from: Date
  until: Date
}

export interface OpenTimespan {
  from?: Date
  until?: Date
}

export function yearAndMonthToTimespan(year: number, month: number): Timespan {
  const date = startOfDay(new Date(year, month - 1, 2))
  const from = startOfDay(startOfMonth(date))
  const until = endOfDay(endOfMonth(date))

  return { from, until }
}

export function yearAndQuarterToTimespan(year: number, quarter: number): Timespan {
  const date = new Date(year, (quarter - 1) * 3, 2)
  const from = startOfDay(startOfMonth(date))
  const until = endOfDay(endOfMonth(add(date, { months: 2 })))

  return { from, until }
}

/**
 * Returns from and until Date objects for a given year as a whole or from the
 * start of the year until the same day of that year as the current date.
 *
 * If the current year is a leap year, the current date is the 29th of February
 * and the requested year is not a leap year, the timespan ends at 28th of
 * February of that year.
 *
 * @param year Year to generate the timespan for
 * @param onlyUntilToday Whether the timespan should only span until the same
 *                       day as today of that year
 * @param now Optional parameter to set the current date
 *
 * @example
 * ```ts
 * yearToTimespan({ year: 2024 })
 * // => { from: 2023-12-31T23:00:00.000Z, until: 2024-12-31T22:59:59.999Z }
 * ```
 *
 * @example only until today
 * ```ts
 * yearToTimespan({ year: 2023, onlyUntilToday: true })
 * // (today: 2024-07-31T09:22:48.958Z)
 * // => { from: 2022-12-31T23:00:00.000Z, until: 2023-07-31T21:59:59.999Z }
 * ```
 */
export function yearToTimespan({
  year,
  onlyUntilToday = false,
  now = new Date(),
}: {
  year: number
  onlyUntilToday?: boolean
  now?: Date
}): Timespan {
  const date = new Date(year, 0, 2)
  const from = startOfYear(date)

  const until = onlyUntilToday ? endOfDay(setYear(now, year)) : endOfYear(date)

  if (onlyUntilToday && until.getMonth() > now.getMonth()) {
    return { from, until: sub(until, { days: 1 }) }
  }

  return { from, until }
}

export function splitTimespanInMonths(timespan: Timespan): Array<Timespan> {
  return eachMonthOfInterval({
    start: timespan.from,
    end: timespan.until,
  }).map((date) => {
    return {
      from: startOfMonth(date),
      until: endOfMonth(date),
    }
  })
}

/**
 * Return `true` if `a` overlaps with `b`
 * Note that it's not an overlap if `a` and `b` only "touch", i.e. a.end === b.start or b.end === a.start
 */
export function overlaps(a: Timespan, b: Timespan): boolean {
  return a.from < b.until && a.until > b.from
}

/**
 * Return `true` if `a` is within `b`
 * Boundaries of `b` are treated inclusively, hence return `true` if `a` is equal to `b`
 */
export function isWithin(a: Timespan, b: Timespan): boolean {
  return a.from >= b.from && a.until <= b.until
}
